import type { Scene } from "../engine/scene/SceneManager";
import type { Game } from "../game/Game";
import { MainMenuScene } from "./MainMenuScene";
import { loadRunHistory, recordDifficulty, type WinRecord } from "../game/RunHistory";
import { getDifficultyConfig } from "../data/difficulty";
import { CHAMPIONS, TIER_COLORS } from "../data/champions";
import { championIconUrl } from "../config/constants";

export class HallOfFameScene implements Scene {
  private root!: HTMLDivElement;

  constructor(private game: Game) {}

  enter(): void {
    this.root = document.createElement("div");
    this.root.className = "reward-screen";

    const title = document.createElement("div");
    title.className = "reward-title";
    title.textContent = "HALL OF FAME";
    this.root.appendChild(title);

    const sub = document.createElement("div");
    sub.style.cssText =
      "font-size:11px;color:rgba(200,170,100,0.55);letter-spacing:2px;margin-top:-10px;margin-bottom:4px;";
    sub.textContent = "TEAMS THAT CONQUERED RUNETERRA";
    this.root.appendChild(sub);

    const history = loadRunHistory();

    if (history.length === 0) {
      const empty = document.createElement("div");
      empty.style.cssText =
        "color:rgba(180,190,210,0.45);font-size:13px;text-align:center;padding:24px 0;";
      empty.textContent = "No victories yet. Conquer Runeterra to write your legend.";
      this.root.appendChild(empty);
    } else {
      const list = document.createElement("div");
      list.style.cssText =
        "display:flex;flex-direction:column;gap:10px;width:100%;max-width:420px;";
      for (const record of history) {
        list.appendChild(this.buildRecord(record));
      }
      this.root.appendChild(list);
    }

    const backBtn = document.createElement("button");
    backBtn.className = "btn ghost btn-sm";
    backBtn.textContent = "BACK";
    backBtn.addEventListener("click", () => {
      this.game.sceneManager.transition(new MainMenuScene(this.game));
    });
    this.root.appendChild(backBtn);

    this.game.sceneRoot.appendChild(this.root);
  }

  private buildRecord(record: WinRecord): HTMLDivElement {
    const card = document.createElement("div");
    card.style.cssText =
      "background:rgba(10,14,20,0.85);border:1px solid rgba(200,170,100,0.2);border-radius:6px;" +
      "padding:10px 14px;display:flex;flex-direction:column;gap:8px;";

    const header = document.createElement("div");
    header.style.cssText = "display:flex;align-items:center;gap:8px;";

    const date = document.createElement("div");
    date.style.cssText = "font-size:10px;color:rgba(180,190,210,0.4);letter-spacing:1px;";
    date.textContent = new Date(record.date).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
    header.appendChild(date);

    const diff = recordDifficulty(record);
    if (diff !== "normal") {
      const isImpossible = diff === "impossible";
      const badge = document.createElement("div");
      badge.style.cssText =
        `font-size:9px;font-weight:700;letter-spacing:2px;color:${isImpossible ? "#ff2828" : "#ff8848"};` +
        `text-shadow:0 0 8px ${isImpossible ? "#ff282866" : "#ff884866"};`;
      badge.textContent = getDifficultyConfig(diff).label;
      header.appendChild(badge);
    }

    card.appendChild(header);

    const champRow = document.createElement("div");
    champRow.style.cssText = "display:flex;gap:10px;align-items:center;flex-wrap:wrap;";
    for (const c of record.champions) {
      champRow.appendChild(this.buildMiniChamp(c));
    }
    card.appendChild(champRow);

    return card;
  }

  private buildMiniChamp(c: WinRecord["champions"][number]): HTMLDivElement {
    const def = CHAMPIONS[c.championId as keyof typeof CHAMPIONS];
    const tierColor = def ? TIER_COLORS[def.tier] : "#9aaabf";
    const starColor = c.stars === 3 ? "#ffe066" : c.stars === 2 ? "#f0c040" : tierColor;
    const ringColor = c.stars >= 2 ? starColor : tierColor;

    const wrap = document.createElement("div");
    wrap.style.cssText = "display:flex;align-items:center;gap:7px;";

    const icon = document.createElement("div");
    const iconUrl = def ? championIconUrl(def.iconId) : "";
    icon.style.cssText =
      `width:40px;height:40px;border-radius:5px;border:1.5px solid ${ringColor};` +
      `background-image:url('${iconUrl}');background-size:cover;background-position:center;` +
      `box-shadow:0 0 6px ${ringColor}44;flex-shrink:0;`;
    wrap.appendChild(icon);

    const info = document.createElement("div");
    const stars = c.stars > 1 ? " " + "★".repeat(c.stars) : "";
    info.innerHTML =
      `<div style="font-size:11px;font-weight:700;color:${tierColor}">${c.displayName}</div>` +
      `<div style="font-size:10px;color:${c.stars >= 2 ? starColor : "rgba(180,190,210,0.5)"}">Lv${c.level}${stars}</div>`;
    wrap.appendChild(info);

    return wrap;
  }

  exit(): void {
    this.root?.remove();
  }

  update(_dt: number): void {}
  render(_ctx: CanvasRenderingContext2D): void {}
}
